import React, { useState } from 'react';
import { Edit2, Trash2, Save, X } from 'lucide-react';
import { formatNumber } from '../utils/helpers';

const CohortManager = ({ cohorts, brands, onAdd, onEdit, onDelete, showToast }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    brand_id: '',
    name: '',
    user_count: 0,
    user_type: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    brand_id: '',
    name: '',
    user_count: 0,
    user_type: ''
  });

  const handleStartEdit = (cohort) => {
    setEditingId(cohort.id);
    setEditForm({
      brand_id: cohort.brand_id,
      name: cohort.name,
      user_count: cohort.user_count,
      user_type: cohort.user_type
    });
  };

  const handleSaveEdit = async () => {
    try {
      await onEdit(editingId, editForm);
      setEditingId(null);
      showToast('Cohort updated successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update cohort', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      brand_id: '',
      name: '',
      user_count: 0,
      user_type: ''
    });
  };

  const handleDelete = async (cohortId) => {
    if (!window.confirm('Are you sure you want to delete this cohort?')) return;
    
    try {
      await onDelete(cohortId);
      showToast('Cohort deleted successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete cohort', 'error');
    }
  };

  const handleAddCohort = async () => {
    if (!addForm.brand_id || !addForm.name || !addForm.user_count || !addForm.user_type) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    try {
      await onAdd(addForm);
      setAddForm({
        brand_id: '',
        name: '',
        user_count: 0,
        user_type: ''
      });
      setShowAddForm(false);
      showToast('Cohort added successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add cohort', 'error');
    }
  };

  // Group cohorts by brand
  const cohortsByBrand = brands.reduce((acc, brand) => {
    acc[brand.id] = cohorts.filter(c => c.brand_id === brand.id);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Add Cohort Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Cohorts by Brand</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          {showAddForm ? <X size={16} /> : <span>+ Add Cohort</span>}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="p-4 bg-slate-900 rounded-lg border border-indigo-500/30">
          <h4 className="text-sm font-semibold text-white mb-3">Add New Cohort</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Brand</label>
              <select
                value={addForm.brand_id}
                onChange={(e) => setAddForm({ ...addForm, brand_id: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-1">Cohort Name</label>
              <input
                type="text"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g., Premium Users"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-1">User Count</label>
              <input
                type="number"
                value={addForm.user_count}
                onChange={(e) => setAddForm({ ...addForm, user_count: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-1">User Type</label>
              <input
                type="text"
                value={addForm.user_type}
                onChange={(e) => setAddForm({ ...addForm, user_type: e.target.value })}
                placeholder="e.g., Premium, Active"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAddCohort}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
            >
              Add Cohort
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cohorts by Brand */}
      {brands.map((brand) => {
        const brandCohorts = cohortsByBrand[brand.id] || [];
        
        return (
          <div key={brand.id} className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: brand.color }}
              />
              <h3 className="text-white font-semibold">{brand.name}</h3>
              <span className="text-sm text-slate-500">
                ({brandCohorts.length} cohort{brandCohorts.length !== 1 ? 's' : ''})
              </span>
            </div>
            
            {brandCohorts.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">No cohorts added yet</p>
            ) : (
              <div className="space-y-2">
                {brandCohorts.map((cohort) => {
                  const isEditing = editingId === cohort.id;
                  
                  return (
                    <div
                      key={cohort.id}
                      className="p-3 bg-slate-800 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                    >
                      {isEditing ? (
                        // Edit Mode
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              placeholder="Cohort Name"
                              className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <input
                              type="number"
                              value={editForm.user_count}
                              onChange={(e) => setEditForm({ ...editForm, user_count: parseInt(e.target.value) || 0 })}
                              placeholder="User Count"
                              className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <input
                              type="text"
                              value={editForm.user_type}
                              onChange={(e) => setEditForm({ ...editForm, user_type: e.target.value })}
                              placeholder="User Type"
                              className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveEdit}
                              className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                            >
                              <Save size={14} />
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="flex items-center gap-1 px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors"
                            >
                              <X size={14} />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="text-white font-medium text-sm">{cohort.name}</p>
                              
                              {/* USED Badge */}
                              {cohort.is_used && (
                                <span className={`
                                  text-xs font-bold px-2 py-1 rounded
                                  ${cohort.used_in_last_month 
                                    ? 'bg-yellow-500 text-black' 
                                    : 'bg-slate-600 text-white'
                                  }
                                `}>
                                  {cohort.used_in_last_month ? '🔥 USED' : 'USED'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {formatNumber(cohort.user_count)} users · {cohort.user_type}
                            </p>
                            
                            {/* Usage stats */}
                            {cohort.usage_count > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                📊 Used {cohort.usage_count} time{cohort.usage_count !== 1 ? 's' : ''}
                                {cohort.last_used_date && ` · Last: ${new Date(cohort.last_used_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleStartEdit(cohort)}
                              className="p-2 text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                              title="Edit cohort"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(cohort.id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              title="Delete cohort"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CohortManager;
